/**
 * Scroll Animations
 * Uses GSAP to create smooth scroll-triggered animations
 */

import gsap from 'gsap'
import { ScrollTrigger } from 'gsap/ScrollTrigger'

// Register GSAP plugins
gsap.registerPlugin(ScrollTrigger)

export function initScrollAnimations() {
  // Animate platform capability cards
  animateCapabilityCards()

  // Animate use cases section
  animateUseCasesSection()

  // Animate image stacks in use cases
  animateImageStacks()

  // Animate CTA section
  animateCTASection()

  // Add parallax effect to hero
  addHeroParallax()
}

function animateCapabilityCards() {
  const cards = gsap.utils.toArray<HTMLElement>('.capability-card')

  cards.forEach((card, index) => {
    gsap.from(card, {
      scrollTrigger: {
        trigger: card,
        start: 'top 95%',
        toggleActions: 'play none none reverse',
      },
      opacity: 0,
      y: 50,
      duration: 0.6,
      delay: index * 0.1,
      ease: 'power2.out',
    })
  })
}

function animateUseCasesSection() {
  const section = document.querySelector('.use-cases')
  if (!section) return

  // Animate section header
  gsap.from('.use-cases .section-header', {
    scrollTrigger: {
      trigger: '.use-cases',
      start: 'top 70%',
      toggleActions: 'play none none reverse',
    },
    opacity: 0,
    y: 40,
    duration: 0.8,
    ease: 'power2.out',
  })

  // Animate vertical tabs
  const tabs = gsap.utils.toArray<HTMLElement>('.vertical-tab')
  tabs.forEach((tab, index) => {
    gsap.from(tab, {
      scrollTrigger: {
        trigger: '.vertical-tabs',
        start: 'top 70%',
        toggleActions: 'play none none reverse',
      },
      opacity: 0,
      x: -30,
      duration: 0.5,
      delay: index * 0.1,
      ease: 'power2.out',
    })
  })
}

function animateImageStacks() {
  const imageStacks = gsap.utils.toArray<HTMLElement>('.image-stack')
  let scrollTimeout: number | null = null
  let isScrolling = false

  // Track scrolling state
  window.addEventListener('scroll', () => {
    isScrolling = true
    if (scrollTimeout) {
      clearTimeout(scrollTimeout)
    }
    scrollTimeout = window.setTimeout(() => {
      isScrolling = false
    }, 150) // Consider scroll stopped after 150ms of no scrolling
  })

  imageStacks.forEach(stack => {
    const modalImage = stack.querySelector<HTMLElement>('.image-stack__modal')
    if (!modalImage) return

    let hasAnimated = false

    // Shared animation function
    const animateModal = () => {
      if (hasAnimated) return

      // Wait for scrolling to stop, then animate
      const checkScrollStop = () => {
        if (!isScrolling) {
          hasAnimated = true
          gsap.to(modalImage, {
            y: '-30%',
            duration: 1.2,
            delay: 0.3,
            ease: 'power2.out',
          })
        } else {
          // Check again in 100ms
          setTimeout(checkScrollStop, 100)
        }
      }
      checkScrollStop()
    }

    // Create ScrollTrigger to detect when element is visible
    ScrollTrigger.create({
      trigger: stack,
      start: 'top 80%',
      end: 'bottom 20%',
      onEnter: animateModal,
      onEnterBack: animateModal,
      onLeave: () => {
        // Reset when scrolling down past the element
        hasAnimated = false
        gsap.to(modalImage, {
          y: 0,
          duration: 0.6,
          ease: 'power2.in'
        })
      },
      onLeaveBack: () => {
        // Reset when scrolling back up past the trigger
        hasAnimated = false
        gsap.to(modalImage, {
          y: 0,
          duration: 0.6,
          ease: 'power2.in'
        })
      }
    })
  })
}

// Export function to trigger animation when tab changes
export function animateImageStackOnTabChange(verticalContent: HTMLElement) {
  const imageStack = verticalContent.querySelector<HTMLElement>('.image-stack')
  if (!imageStack) return

  const modalImage = imageStack.querySelector<HTMLElement>('.image-stack__modal')
  if (!modalImage) return

  // Reset and animate with delay and slower duration
  gsap.fromTo(
    modalImage,
    { y: 0 },
    { y: '-30%', duration: 1.2, delay: 0.5, ease: 'power2.out' }
  )
}

function animateCTASection() {
  const ctaSection = document.querySelector('.cta-section')
  if (!ctaSection) return

  gsap.from('.cta-content > *', {
    scrollTrigger: {
      trigger: ctaSection,
      start: 'top 70%',
      toggleActions: 'play none none reverse',
    },
    opacity: 0,
    y: 30,
    duration: 0.6,
    stagger: 0.15,
    ease: 'power2.out',
  })
}

function addHeroParallax() {
  const background = document.querySelector('.hero__background')
  if (!background) return

  // Subtle fade on scroll
  gsap.to(background, {
    scrollTrigger: {
      trigger: '.hero',
      start: 'top top',
      end: 'bottom top',
      scrub: 1,
    },
    opacity: 0.4,
    ease: 'none',
  })
}
